import { AIResultType } from '../../../../@types/enums';
import { useViewerStore } from '../../../../store/store';
import {
    fetchAIResult,
    getSalesforceIdForAiCache,
    loadLocalAiResultCacheWithFallback,
    saveLocalAiResultCache,
} from '../../../../services/geminiService';
import { logger } from '../../../../services/loggingService';
import {
    applyAiScenariosCacheToViewerStore,
    buildAiScenariosCacheBlobForLocalPersist,
    hasMeaningfulAiScenariosPayload,
    hasUsableAiDesignCatalogInStore,
    hasValidSnapshotMapData,
    hydrateLocalAiResultCacheToViewerStore,
    mergeSnapshotSidecarsIntoViewerStore,
} from './utils/aiLayoutCacheHydration';

type LoadSavedDesignsArgs = {
    setLoadingSavedResult: (next: boolean) => void;
    setAiLoadingStep: (next: string) => void;
    setSavedAIDesigns: (next: any[]) => void;
    setLayoutModulesByKey: (next: Record<string, number>) => void;
    setLayoutProductionByKey: (next: Record<string, number>) => void;
    setLayoutMetricsByKey: (next: Record<string, any>) => void;
    setAiScenariosSectionVisible: (next: boolean) => void;
    setResultCardMinimized: (next: boolean) => void;
    setIsLoadingSnapshots: (next: boolean) => void;
    setAiLoading: (next: boolean) => void;
    /** When false, skip loading UI while still checking GetAiResult for saved designs. */
    showLoading?: boolean;
};

async function hydrateFromLocalCacheFallback(sfid: string | null): Promise<boolean> {
    if (!sfid) return false;
    return hydrateLocalAiResultCacheToViewerStore(sfid);
}

async function runLoadSavedDesignsActionImpl({
    setLoadingSavedResult,
    setAiLoadingStep,
    setSavedAIDesigns,
    setLayoutModulesByKey,
    setLayoutProductionByKey,
    setLayoutMetricsByKey,
    setAiScenariosSectionVisible,
    setResultCardMinimized,
    setIsLoadingSnapshots,
    showLoading = true,
}: LoadSavedDesignsArgs): Promise<boolean> {
    if (Boolean((useViewerStore.getState() as any).aiLoading)) {
        return false;
    }
    try {
        const sfid = getSalesforceIdForAiCache();

        if (showLoading) {
            setLoadingSavedResult(true);
            setAiLoadingStep('Loading Saved AI Designs...');
        }

        const local = await loadLocalAiResultCacheWithFallback(sfid);
        const localHasScenarioDataEarly = hasMeaningfulAiScenariosPayload(local?.aiScenarios);
        if (localHasScenarioDataEarly && sfid) {
            await hydrateFromLocalCacheFallback(sfid);
        }

        const remote = await fetchAIResult(
            [AIResultType.aiScenarios, AIResultType.treeCuttingScenarios, AIResultType.aiWishlist],
            { bypassCache: true },
        );

        const remoteHasScenarioData = hasMeaningfulAiScenariosPayload(remote?.aiScenarios);
        const localHasScenarioData = localHasScenarioDataEarly;
        const remoteHasTreeCutting =
            remote.treeCuttingScenarios &&
            Object.keys((remote.treeCuttingScenarios as any) ?? {}).length > 0;
        const remoteHasWishlist = Array.isArray(remote.aiWishlist) && remote.aiWishlist.length > 0;

        const useDbScenarios = remoteHasScenarioData;
        const useCacheScenarios = !useDbScenarios && localHasScenarioData;
        const aiDesignsDataSource: 'cache' | 'db' | null = useDbScenarios
            ? 'db'
            : useCacheScenarios
              ? 'cache'
              : null;

        if (!useDbScenarios && !useCacheScenarios) {
            if (await hydrateFromLocalCacheFallback(sfid)) {
                const localAfter = await loadLocalAiResultCacheWithFallback(sfid);
                setSavedAIDesigns((localAfter?.aiWishlist as any) ?? []);
                setAiScenariosSectionVisible(true);
                setResultCardMinimized(false);
                return true;
            }
            setAiScenariosSectionVisible(true);
            setResultCardMinimized(false);
            return true;
        }

        const resolvedAiScenarios = useDbScenarios
            ? remote.aiScenarios
            : local?.aiScenarios ?? null;

        const shouldFetchSnapshotsFromDb = useDbScenarios;

        const data = useCacheScenarios
            ? ({
                  layoutSnapshots: local?.layoutSnapshots ?? remote.layoutSnapshots ?? null,
                  variantSnapshots: local?.variantSnapshots ?? remote.variantSnapshots ?? null,
                  aiScenarios: resolvedAiScenarios,
                  treeCuttingScenarios:
                      (local?.treeCuttingScenarios as Record<string, unknown> | null | undefined) ??
                      remote.treeCuttingScenarios ??
                      null,
                  aiWishlist: (local?.aiWishlist as any) ?? remote.aiWishlist ?? [],
                  aiResult:
                      typeof local?.aiResult === 'string' && local.aiResult.trim()
                          ? local.aiResult
                          : typeof (remote as any)?.aiResult === 'string'
                            ? (remote as any).aiResult
                            : null,
              } as any)
            : ({
                  layoutSnapshots:
                      remote.layoutSnapshots ??
                      (shouldFetchSnapshotsFromDb ? null : (local?.layoutSnapshots ?? null)),
                  variantSnapshots:
                      remote.variantSnapshots ??
                      (shouldFetchSnapshotsFromDb ? null : (local?.variantSnapshots ?? null)),
                  aiScenarios: resolvedAiScenarios,
                  treeCuttingScenarios: remoteHasTreeCutting
                      ? remote.treeCuttingScenarios
                      : (local?.treeCuttingScenarios ?? null),
                  aiWishlist: remoteHasWishlist ? remote.aiWishlist : ((local?.aiWishlist as any) ?? []),
                  aiResult:
                      typeof (remote as any)?.aiResult === 'string' && (remote as any).aiResult.trim()
                          ? (remote as any).aiResult
                          : typeof local?.aiResult === 'string'
                            ? local.aiResult
                            : null,
              } as any);

        if (!hasMeaningfulAiScenariosPayload(data.aiScenarios)) {
            if (await hydrateFromLocalCacheFallback(sfid)) {
                const localAfter = await loadLocalAiResultCacheWithFallback(sfid);
                setSavedAIDesigns((localAfter?.aiWishlist as any) ?? []);
                setAiScenariosSectionVisible(true);
                setResultCardMinimized(false);
                return true;
            }
            setAiScenariosSectionVisible(true);
            setResultCardMinimized(false);
            return true;
        }

        const aiScenarios = data.aiScenarios;
        const currentState = useViewerStore.getState() as any;
        const resolvedAiResult =
            typeof (data as any).aiResult === 'string'
                ? (data as any).aiResult
                : typeof currentState.aiResult === 'string'
                  ? currentState.aiResult
                  : '';

        applyAiScenariosCacheToViewerStore({
            aiScenarios,
            layoutSnapshots: (data.layoutSnapshots as Record<string, unknown> | null | undefined) ?? null,
            variantSnapshots: (data.variantSnapshots as Record<string, unknown> | null | undefined) ?? null,
            treeCuttingScenarios: data.treeCuttingScenarios ?? {},
            aiResult: resolvedAiResult,
            aiDesignsDataSource: aiDesignsDataSource ?? undefined,
        });

        mergeSnapshotSidecarsIntoViewerStore({
            layoutSnapshots: useCacheScenarios
                ? ((local?.layoutSnapshots ?? data.layoutSnapshots) as Record<string, unknown> | null)
                : ((remote.layoutSnapshots ?? local?.layoutSnapshots) as Record<string, unknown> | null),
            variantSnapshots: useCacheScenarios
                ? ((local?.variantSnapshots ?? data.variantSnapshots) as Record<string, unknown> | null)
                : ((remote.variantSnapshots ?? local?.variantSnapshots) as Record<string, unknown> | null),
        });

        if (!hasUsableAiDesignCatalogInStore() && localHasScenarioData && sfid) {
            await hydrateFromLocalCacheFallback(sfid);
        }

        const stateAfterInitialMerge = useViewerStore.getState() as any;
        const storeHasSnapshots =
            hasValidSnapshotMapData(stateAfterInitialMerge.layoutSnapshotsByKey) ||
            hasValidSnapshotMapData(stateAfterInitialMerge.aiLayoutVariantSnapshots);
        const needsDeferredSnapshots = shouldFetchSnapshotsFromDb && !storeHasSnapshots;

        if (needsDeferredSnapshots) {
            setIsLoadingSnapshots(true);
        }

        setSavedAIDesigns(data.aiWishlist ?? []);
        setAiScenariosSectionVisible(true);
        setResultCardMinimized(false);

        if (hasUsableAiDesignCatalogInStore() && useDbScenarios) {
            await saveLocalAiResultCache({
                layoutSnapshots:
                    (useViewerStore.getState() as any).layoutSnapshotsByKey ?? data.layoutSnapshots ?? null,
                variantSnapshots:
                    (useViewerStore.getState() as any).aiLayoutVariantSnapshots ?? data.variantSnapshots ?? null,
                aiScenarios: data.aiScenarios ?? null,
                treeCuttingScenarios: data.treeCuttingScenarios ?? null,
                aiWishlist: data.aiWishlist ?? null,
                aiResult: resolvedAiResult || null,
            });
        }

        if (needsDeferredSnapshots) {
            void (async () => {
                try {
                    const snapshots = await fetchAIResult(undefined, { bypassCache: true });
                    const applied = mergeSnapshotSidecarsIntoViewerStore({
                        layoutSnapshots: snapshots.layoutSnapshots as Record<string, unknown> | null,
                        variantSnapshots: snapshots.variantSnapshots as Record<string, unknown> | null,
                    });
                    if (!applied) return;

                    const stateAfter = useViewerStore.getState() as any;
                    if (!hasUsableAiDesignCatalogInStore(stateAfter)) return;
                    void saveLocalAiResultCache({
                        layoutSnapshots: stateAfter.layoutSnapshotsByKey ?? null,
                        variantSnapshots: stateAfter.aiLayoutVariantSnapshots ?? null,
                        aiScenarios: buildAiScenariosCacheBlobForLocalPersist(),
                        treeCuttingScenarios:
                            stateAfter.aiModuleFillingTreeScenariosByLayoutKey ??
                            data.treeCuttingScenarios ??
                            null,
                        aiWishlist: data.aiWishlist ?? null,
                        aiResult: resolvedAiResult || null,
                    });
                } catch (snapshotError) {
                    logger.error(snapshotError);
                } finally {
                    setIsLoadingSnapshots(false);
                }
            })();
        }
    } catch (e) {
        setIsLoadingSnapshots(false);
        logger.error(e);
        try {
            const sfid = getSalesforceIdForAiCache();
            if (await hydrateFromLocalCacheFallback(sfid)) {
                const local = await loadLocalAiResultCacheWithFallback(sfid);
                setSavedAIDesigns((local?.aiWishlist as any) ?? []);
                if (local?.aiScenarios) {
                    setLayoutModulesByKey((local.aiScenarios as any)?.layoutModulesByKey ?? {});
                    setLayoutProductionByKey((local.aiScenarios as any)?.layoutProductionByKey ?? {});
                    setLayoutMetricsByKey((local.aiScenarios as any)?.layoutMetricsByKey ?? {});
                }
            }
        } catch {
            // ignore local cache fallback errors
        } finally {
            setAiScenariosSectionVisible(true);
            setResultCardMinimized(false);
        }
    } finally {
        setLoadingSavedResult(false);
        if (!Boolean((useViewerStore.getState() as any).aiLoading)) {
            setAiLoadingStep('');
        }
    }
    return true;
}

let loadSavedDesignsInFlight: Promise<boolean> | null = null;

export async function runLoadSavedDesignsAction(args: LoadSavedDesignsArgs): Promise<boolean> {
    if (loadSavedDesignsInFlight) {
        args.setLoadingSavedResult(true);
        try {
            return await loadSavedDesignsInFlight;
        } finally {
            args.setLoadingSavedResult(false);
        }
    }

    loadSavedDesignsInFlight = runLoadSavedDesignsActionImpl(args).finally(() => {
        loadSavedDesignsInFlight = null;
    });

    return loadSavedDesignsInFlight;
}
